/*======================================================================================================================
  * Bluebell: src/app/state/template-cases/template-case.effects.ts
  * Last Edited: 20/01/2025
  * Copyright 2024-2025 Bramble CFD LTD
  * The contents of this file are NOT for redistribution
  * See AUTHORS for list of developers on this project
 =====================================================================================================================*/
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Update } from '@ngrx/entity';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { EffectHandlerService } from '@services/effect-handler.service';
import { programmeProjectSelectionUnapplied } from '@state/account/account.actions';
import { ApiService } from '@state/api.service';
import { templateCaseActions, templateCaseEntityActions } from '@state/template-cases/template-case.actions';
import {
  TemplateCase,
  TemplateCaseUpload,
  DropdownItem,
  FeatureColumn,
  TemplateFeature,
  TemplateTab,
  DropdownField,
  SelectedTab,
  MethodologyGroup, TemplateCaseImport,
} from '@state/template-cases/template-case.interfaces';
import {
  selectLoadInactiveTemplateCases,
  selectSelectedTemplateCase,
  selectTemplateCaseFeatures,
} from '@state/template-cases/template-case.selectors';
import { TemplateCaseService } from '@app/pages/template-cases/template-case.service';
import { MessageService } from 'primeng/api';
import { of } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';


@Injectable()
export class TemplateCaseEffects {
  private actions$ = inject(Actions);
  private templateCaseService = inject(TemplateCaseService);
  private msgService = inject(MessageService);
  private store = inject(Store);
  private api = inject(ApiService);
  private handler = inject(EffectHandlerService);


  loadInactive = this.store.selectSignal(selectLoadInactiveTemplateCases);

  /* Create Actions */

  upload$ = createEffect(() => this.actions$.pipe(
    ofType(templateCaseActions.upload),
    map(({ programme, name, description, methodology_group, workflows, file }: TemplateCaseUpload) =>
      this.templateCaseService.upload(programme, name, description, methodology_group, workflows, file)),
  ), { dispatch: false });

  uploadSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(templateCaseActions.uploadSuccess),
    tap(({ name }: TemplateCase) => this.msgService.add({
      severity: 'success',
      summary: 'Upload Success',
      detail: `Successfully Uploaded Template Case ${ name }`,
      life: 8000,
    })),
    map((templateCase) => templateCaseEntityActions.addOne(templateCase)),
  ));

  uploadFailure$ = createEffect(() => this.actions$.pipe(
    ofType(templateCaseActions.uploadFailure),
    tap(({ templateCaseName, error }) => this.msgService.add({
      severity: 'error',
      summary: 'Error Uploading Template Case',
      detail: `Failed to Upload Template Case ${ templateCaseName }: ${ error }`,
      life: 8000,
    })),
  ), { dispatch: false });

  clone$ = createEffect(() => this.actions$.pipe(
    ofType(templateCaseActions.clone),
    switchMap(({ templateCase, data }) => this.api.cloneTemplateCase(templateCase.uuid, data).pipe(
      tap((clonedTemplateCase: TemplateCase) => this.templateCaseService.clonedCaseSubject$.next(clonedTemplateCase)),
      this.handler.effectResponse({
        retry: templateCaseActions.clone({ templateCase, data }),
        success: (returnedTemplateCase: TemplateCase) => templateCaseActions.cloneSuccess(returnedTemplateCase),
        failure: ({ error }: HttpErrorResponse) => templateCaseActions.cloneFailure({
          templateCaseName: templateCase.name, error: error.detail
        }),
        failureMessage: 'Could not clone Template Case',
      })
    )),
  ));

  cloneSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(templateCaseActions.cloneSuccess),
    tap((templateCase: TemplateCase) => this.msgService.add({
      severity: 'success',
      summary: 'Copy Success',
      detail: `Successfully Copied Template Case ${ templateCase.name }`,
      life: 8000,
    })),
    map((templateCase) => templateCaseEntityActions.addOne(templateCase)),
  ));

  cloneFailure = createEffect(() => this.actions$.pipe(
    ofType(templateCaseActions.cloneFailure),
    tap(({ templateCaseName, error }) => this.msgService.add({
      severity: 'error',
      summary: 'Error Copying Template Case',
      detail: `Failed to Copy Template Case ${ templateCaseName }: ${ error }`,
      life: 8000,
    })),
  ), { dispatch: false });

  import = createEffect(() => this.actions$.pipe(
    ofType(templateCaseActions.import),
    switchMap((data: TemplateCaseImport) => this.api.importTemplateCases(data).pipe(
      this.handler.effectResponse({
        retry: templateCaseActions.import(data),
        success: (templateCases: TemplateCase[]) => templateCaseActions.importSuccess({ templateCases }),
        failure: templateCaseActions.importFailure(),
        failureMessage: 'Could not import Template Cases',
      })
    )),
  ));

  importSuccess = createEffect(() => this.actions$.pipe(
    ofType(templateCaseActions.importSuccess),
    tap(() => {
      this.msgService.add({
        severity: 'success',
        summary: 'Import Success',
        detail: `Successfully Imported Template Cases`,
        life: 8000,
      });
    }),
    map(({ templateCases }) => templateCaseEntityActions.addMany({ templateCases })),
  ))

  /*
      Retrieve Actions
   */

  loadAll$ = createEffect(() => this.actions$.pipe(
    ofType(
      templateCaseActions.loadAll,
      templateCaseActions.toggleLoadInactive,
    ),
    switchMap(() => this.api.listTemplateCases(!this.loadInactive()).pipe(
      this.handler.effectResponse({
        retry: templateCaseActions.loadAll(),
        success: (templateCases: TemplateCase[]) => templateCaseActions.loadAllSuccess(templateCases),
        failure: templateCaseActions.loadAllFailure(),
        failureMessage: 'Could not retrieve Template Cases',
      })
    )),
  ));

  loadUsedInRuns$ = createEffect(() => this.actions$.pipe(
    ofType(templateCaseActions.loadUsedInRuns),
    switchMap((templateCase: TemplateCase) => this.api.templateCaseUsedInRuns(templateCase.uuid).pipe(
      this.handler.effectResponse({
        retry: templateCaseActions.loadUsedInRuns(templateCase),
        success: (usedInRuns: string[]) => templateCaseActions.loadUsedInRunsSuccess(usedInRuns),
        failure: ({ error }: HttpErrorResponse) => templateCaseActions.loadUsedInRunsFailure({
          templateCaseName: templateCase.name, error: error.detail
        }),
      })
    )),
  ));

  loadPythonData$ = createEffect(() => this.actions$.pipe(
    ofType(templateCaseActions.loadPythonData),
    switchMap((templateCase: TemplateCase) => this.api.templateCasePythonData(templateCase.uuid).pipe(
      this.handler.effectResponse({
        retry: templateCaseActions.loadPythonData(templateCase),
        success: (pythonData: string) => templateCaseActions.loadPythonDataSuccess(pythonData),
        failure: ({ error }: HttpErrorResponse) => templateCaseActions.loadPythonDataFailure({
          templateCaseName: templateCase.name, error: error.detail
        }),
        failureMessage: 'Could not retrieve python data for Template Case',
      })
    )),
  ));

  ensureTabsLoaded$ = createEffect(() => this.actions$.pipe(
    ofType(templateCaseActions.loadTemplateFeaturesSuccess),
    concatLatestFrom(() => this.store.select(selectSelectedTemplateCase)),
    filter(([, templateCase]) => !!templateCase),
    map(([, templateCase]) =>
      templateCaseActions.loadSelectedTemplateTabs(templateCase as TemplateCase)
    ),
  ));

  loadSelectedTemplateTabs$ = createEffect(() => this.actions$.pipe(
    ofType(templateCaseActions.loadSelectedTemplateTabs),
    concatLatestFrom(() => [
      this.store.select(selectTemplateCaseFeatures),
    ]),
    switchMap(([templateCase, templateFeatures]) => {
      let selectedTabs: SelectedTab[] = [];

      const selectedMethodologyGroup: string = templateCase.methodology_group;
      const selectedWorkflowNames: string[] = templateCase.workflows || [];

      let selectedFeatures = templateFeatures.filter((feature: TemplateFeature) => {
        return feature.methodology_groups.find((m) => m.name === selectedMethodologyGroup) ||
               feature.workflows.some((w) => selectedWorkflowNames.find(selectedWorkflow => w.name === selectedWorkflow));
      });

      selectedFeatures = this.templateCaseService.filterFeaturesByAccountSettings(selectedFeatures);
      selectedFeatures.sort((a, b) => a.ordering_key - b.ordering_key);

      selectedFeatures.forEach((feature) => {
        const featureTab = selectedTabs.find((tab) => tab.key === feature.tab.key);
        if (!featureTab) {
          selectedTabs.push({
            'name': feature.tab.name,
            'key': feature.tab.key,
            'features': [feature],
            'gui_viewer': feature.tab.gui_viewer,
            'model_viewer': feature.tab.model_viewer,
            'ordering_key': feature.tab.ordering_key,
          });
        } else {
          featureTab.features.push(feature);
        }
      });

      if (templateCase.methodology_group === 'Flow360') {
        // Need to filter out the normal surface for flow360 case.
        selectedTabs = selectedTabs.map(tab => ({
          ...tab,
          features: tab.features.filter(feature => feature.key !== 'surface')
        }));
      }

      selectedTabs.sort((a, b) => a.ordering_key - b.ordering_key);
      selectedTabs = this.templateCaseService.filterTabsByAccountSettings(selectedTabs);
      return of(selectedTabs).pipe(
        this.handler.effectResponse({
          retry: templateCaseActions.loadSelectedTemplateTabs(templateCase),
          success: (filteredTabs: SelectedTab[]) => templateCaseActions.loadSelectedTemplateTabsSuccess(filteredTabs),
          failure: ({ error }: HttpErrorResponse) => templateCaseActions.loadSelectedTemplateTabsFailure({
            templateCaseName: templateCase.name, error: error.detail
          }),
        })
      );
    })
  ));

  refreshSelectedTemplateTabs$ = createEffect(() => this.actions$.pipe(
    ofType(templateCaseEntityActions.updateOne),
    concatLatestFrom(() => this.store.select(selectSelectedTemplateCase)),
    filter(([{ update }, selectedTemplateCase]) => !!selectedTemplateCase && update.id === selectedTemplateCase?.uuid),
    map(([, selectedTemplateCase]) => templateCaseActions.select(selectedTemplateCase as TemplateCase)),
  ));

  loadSelectedExtraExportsFeatures$ = createEffect(() => this.actions$.pipe(
    ofType(templateCaseActions.loadSelectedExtraExportsFeatures),
    concatLatestFrom(() => [
      this.store.select(selectTemplateCaseFeatures),
    ]),
    switchMap(([attitude, templateFeatures]) => {
      const selectedMethodologyName: string = attitude.cfd_settings.methodology.name;
      const selectedWorkflowName: string = attitude.cfd_settings.workflow.name;

      let selectedFeatures = templateFeatures.filter((feature: TemplateFeature) => {
        return (feature.methodology_groups.find((g) => g.methodologies.map(m => m.name).find(n => n === selectedMethodologyName))
                || feature.workflows.some((w) => w.name === selectedWorkflowName))
               && (feature.tab.key === 'exports' || feature.tab.key === 'extra_exports')
               && !(['export_movies', 'export_surfaces'].includes(feature.key));
      });
      selectedFeatures = this.templateCaseService.filterFeaturesByAccountSettings(selectedFeatures);

      selectedFeatures.sort((a, b) => a.ordering_key - b.ordering_key);

      return of(selectedFeatures).pipe(
        this.handler.effectResponse({
          retry: templateCaseActions.loadSelectedExtraExportsFeatures(attitude),
          success: (filteredFeatures: TemplateFeature[]) => templateCaseActions.loadSelectedExtraExportsFeaturesSuccess(filteredFeatures),
          failure: () => templateCaseActions.loadSelectedExtraExportsFeaturesFailure(),
          failureMessage: 'Could not retrieve selected extra exports features for Template Case',
        })
      );
    })
  ));

  loadSelectedTemplateCaseDataFailure$ = createEffect(() => this.actions$.pipe(
    ofType(templateCaseActions.loadPythonDataFailure),
    tap(({ templateCaseName, error }) => this.msgService.add({
      severity: 'error',
      summary: 'Error Loading Template Case',
      detail: `Failed to load python data for Template Case ${ templateCaseName }: ${ error }`,
      life: 8000,
    })),
  ), { dispatch: false });

  loadGUITemplateData$ = createEffect(() => this.actions$.pipe(
    ofType(templateCaseActions.loadGUITemplateData),
    switchMap((templateCase: TemplateCase) => this.api.templateCaseTemplateData(templateCase.uuid).pipe(
      this.handler.effectResponse({
        retry: templateCaseActions.loadGUITemplateData(templateCase),
        success: (templateResponse: string) => templateCaseActions.loadGUITemplateDataSuccess(templateResponse),
        failure: ({ error }: HttpErrorResponse) => templateCaseActions.loadGUITemplateDataFailure({
          templateCaseName: templateCase.name, error: error.detail
        }),
      })
    )),
  ));

  loadGUITemplateDataFailure$ = createEffect(() => this.actions$.pipe(
    ofType(templateCaseActions.loadGUITemplateDataFailure),
    map(({ templateCaseName, error }) => this.msgService.add({
      severity: 'error',
      summary: 'Error Loading Template Case',
      detail: `Failed to load template data for Template Case ${ templateCaseName }: ${ error }`,
      life: 8000,
    })),
  ), { dispatch: false });

  /* Update Actions */

  update$ = createEffect(() => this.actions$.pipe(
    ofType(templateCaseActions.update),
    map((templateCase: Partial<TemplateCase> & { uuid: string }) => ({...templateCase, workflows: templateCase.workflows ?? []})),
    switchMap((templateCase: Partial<TemplateCase> & { uuid: string }) => this.api.updateTemplateCase(templateCase).pipe(
      this.handler.effectResponse({
        retry: templateCaseActions.update(templateCase),
        success: (returnedTemplateCase: TemplateCase) => templateCaseActions.updateSuccess(returnedTemplateCase),
        failure: ({ error }: HttpErrorResponse) =>  templateCaseActions.updateFailure({
          templateCaseName: templateCase?.name || '',
          error: error.detail
        }),
        failureMessage: 'Could not update Template Case',
      })
    )),
  ));

  updateData$ = createEffect(() => this.actions$.pipe(
    ofType(templateCaseActions.updateData),
    switchMap(({ templateCase, changes }: { templateCase: TemplateCase, changes: any }) =>
      this.api.updateTemplateCasePythonData(templateCase.uuid, changes).pipe(
        this.handler.effectResponse({
          retry: templateCaseActions.updateData({ templateCase, changes }),
          success: (returnedTemplateCase: TemplateCase) => templateCaseActions.updateDataSuccess(returnedTemplateCase),
          failure: ({ error }: HttpErrorResponse) => templateCaseActions.updateDataFailure({
            templateCaseName: templateCase.name, error: error.detail
          }),
        })
      )),
  ));

  updateGUITemplate$ = createEffect(() => this.actions$.pipe(
    ofType(templateCaseActions.updateGUITemplate),
    switchMap(({ templateCase, changes }: { templateCase: TemplateCase, changes: string }) =>
      this.api.updateTemplateCaseTemplateData(templateCase.uuid, changes).pipe(
        this.handler.effectResponse({
          retry: templateCaseActions.updateGUITemplate({ templateCase, changes }),
          success: (returnedTemplateCase: TemplateCase) => templateCaseActions.updateGUITemplateSuccess(returnedTemplateCase),
          failure: ({ error }: HttpErrorResponse) => templateCaseActions.updateGUITemplateFailure({
            templateCaseName: templateCase.name, error: error.detail
          }),
        })
      )),
  ));

  toggleActiveState$ = createEffect(() => this.actions$.pipe(
    ofType(templateCaseActions.toggleActiveState),
    switchMap(({ active, ...rest }: TemplateCase) => this.api.updateTemplateCase({ ...rest, active: !active }).pipe(
      this.handler.effectResponse({
        retry: templateCaseActions.toggleActiveState({ active, ...rest }),
        success: (returnedTemplateCase: TemplateCase) => templateCaseActions.toggleActiveStateSuccess(returnedTemplateCase),
        failure: ({ error }: HttpErrorResponse) => templateCaseActions.toggleActiveStateFailure({
          templateCaseName: rest.name, error: error.detail
        }),
      })
    )),
  ));

  onUpdateTemplateCaseEntity$ = createEffect(() => this.actions$.pipe(
    ofType(templateCaseEntityActions.updateOne),
    filter(({ triggerReload }) => triggerReload),
    concatLatestFrom(() => this.store.select(selectSelectedTemplateCase)),
    filter(([{ update }, selectedTemplateCase]) => !!selectedTemplateCase && update.id === selectedTemplateCase?.uuid),
    switchMap(([, selectedTemplateCase]) => {
      return of(
        templateCaseActions.loadPythonData(selectedTemplateCase as TemplateCase),
        templateCaseActions.loadGUITemplateData(selectedTemplateCase as TemplateCase),
      );
    }),
  ));

  updateSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(templateCaseActions.updateSuccess),
    tap(({ name }: TemplateCase) => this.msgService.add({
      severity: 'success',
      summary: 'Update Success',
      detail: `Updated Template Case ${ name } successfully.`,
      life: 8000,
    })),
    map(({ uuid: id, ...changes }) => templateCaseEntityActions.updateOne({ id, changes }, false)),
  ));

  updateDataSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(
      templateCaseActions.updateDataSuccess,
      templateCaseActions.updateGUITemplateSuccess,
      templateCaseActions.toggleActiveStateSuccess,
    ),
    tap(({ name }: Partial<TemplateCase>) => {
      return this.msgService.add({
        severity: 'success',
        summary: 'Update Success',
        detail: `Updated Template Case ${name} successfully.`,
        life: 8000,
      });
    }),
    map(({ uuid: id, ...changes }) => templateCaseEntityActions.updateOne({ id, changes } as Update<TemplateCase>, true)),
  ));

  updateFailure$ = createEffect(() => this.actions$.pipe(
    ofType(
      templateCaseActions.updateFailure,
      templateCaseActions.updateDataFailure,
      templateCaseActions.updateGUITemplateFailure,
      templateCaseActions.toggleActiveStateFailure,
    ),
    tap(({ templateCaseName, error }) => {
      this.msgService.add({
        severity: 'error',
        summary: 'Update Fail',
        detail: `Failed to Update Template Case ${ templateCaseName }: ${ error }`,
        life: 8000,
      });
    }),
  ), { dispatch: false });

  /* Destroy Actions */

  delete$ = createEffect(() => this.actions$.pipe(
    ofType(templateCaseActions.delete),
    switchMap((templateCase: TemplateCase) => this.api.deleteTemplateCase(templateCase.uuid).pipe(
      this.handler.effectResponse({
        retry: templateCaseActions.delete(templateCase),
        success: () => templateCaseActions.deleteSuccess(templateCase),
        failure: ({ error }: HttpErrorResponse) =>
          templateCaseActions.deleteFailure({ templateCaseName: templateCase.name, error: error.detail }),
        failureMessage: 'Could not delete Template Case',
      })
    )),
  ));

  deleteSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(templateCaseActions.deleteSuccess),
    tap(({ name }: TemplateCase) => this.msgService.add({
      severity: 'success',
      summary: 'Upload Success',
      detail: `Deleted Template Case ${ name } successfully.`,
      life: 8000,
    })),
    map(({ uuid }) => templateCaseEntityActions.removeOne(uuid)),
  ));

  deleteFailure$ = createEffect(() => this.actions$.pipe(
    ofType(templateCaseActions.deleteFailure),
    map(({ templateCaseName, error }) => this.msgService.add({
      severity: 'error',
      summary: 'Upload Fail',
      detail: `Failed to Delete Template Case ${ templateCaseName }: ${ error }`,
      life: 8000,
    })),
  ), { dispatch: false });

  /* Other */

  selectTemplateCase$ = createEffect(() => this.actions$.pipe(
    ofType(templateCaseActions.select),
    switchMap((templateCase: TemplateCase) => of(
      templateCaseActions.loadPythonData(templateCase),
      templateCaseActions.loadGUITemplateData(templateCase),
      templateCaseActions.loadSelectedTemplateTabs(templateCase)
    )),
  ));

  triggerLoadUsedInRuns$ = createEffect(() => this.actions$.pipe(
    ofType(templateCaseActions.select),
    filter(({ locked }: TemplateCase) => locked),
    switchMap((templateCase: TemplateCase) => of(
      templateCaseActions.loadUsedInRuns(templateCase),
    )),
  ));

  replaceFile$ = createEffect(() => this.actions$.pipe(
    ofType(templateCaseActions.replaceFile),
    map(({ templateCase, file }) => this.templateCaseService.replaceFile(templateCase, file)),
  ), { dispatch: false });

  replaceFileSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(templateCaseActions.replaceFileSuccess),
    tap(({ name }: TemplateCase) => this.msgService.add({
      severity: 'success',
      summary: 'Upload Success',
      detail: `Replaced Template Case ${ name } successfully.`,
      life: 8000,
    })),
    switchMap(({ uuid: id, ...changes }: TemplateCase) => of(templateCaseEntityActions.updateOne({ id, changes }, true))),
  ));

  replaceFileFailure$ = createEffect(() => this.actions$.pipe(
    ofType(templateCaseActions.replaceFileFailure),
    map(({ templateCaseName, error }) => this.msgService.add({
      severity: 'error',
      summary: 'Upload Fail',
      detail: `Failed to upload Template Case ${ templateCaseName }: ${ error }`,
      life: 8000,
    })),
  ), { dispatch: false });

  replaceData$ = createEffect(() => this.actions$.pipe(
    ofType(templateCaseActions.replaceData),
    switchMap(({ uuid, data }: { uuid: string, data: any }) => this.api.replaceTemplateCaseData(uuid, data).pipe(
      this.handler.effectResponse({
        retry: templateCaseActions.replaceData({ uuid, data }),
        success: (returnedTemplateCase: TemplateCase) => templateCaseActions.replaceDataSuccess(returnedTemplateCase),
        failure: ({ error }: HttpErrorResponse) => templateCaseActions.replaceDataFailure({
          error: error.detail
        }),
        failureMessage: 'Could not replace Template Case data',
      })
    )),
  ));

  replaceDataSuccess$ = createEffect(() => this.actions$.pipe(
    ofType(templateCaseActions.replaceDataSuccess),
    tap(({ name }: TemplateCase) => this.msgService.add({
      severity: 'success',
      summary: 'Upload Success',
      detail: `Replaced Template Case ${ name } successfully.`,
      life: 8000,
    })),
    switchMap(({ uuid: id, ...changes }: TemplateCase) => of(templateCaseEntityActions.updateOne({ id, changes }, true))),
  ));

  replaceDataFailure$ = createEffect(() => this.actions$.pipe(
    ofType(templateCaseActions.replaceDataFailure),
    map(({ error }) => this.msgService.add({
      severity: 'error',
      summary: 'Upload Fail',
      detail: `Failed to upload Template Case: ${ error }`,
      life: 8000,
    })),
  ), { dispatch: false });

  replaceTemplateMethodologyGroup = createEffect(() => this.actions$.pipe(
    ofType(templateCaseActions.replaceTemplateMethodologyGroup),
    switchMap(({templateCase, data}: {templateCase: TemplateCase, data: string}) => of(
      templateCaseActions.replaceData({ uuid: templateCase.uuid , data }),
      templateCaseActions.select(templateCase),
    )),
  ));

  loadDropdownItems$ = createEffect(() => this.actions$.pipe(
    ofType(templateCaseActions.loadDropdownItems),
    switchMap(() => this.api.templateCaseDropdownItems().pipe(
      this.handler.effectResponse({
        retry: templateCaseActions.loadDropdownItems(),
        success: (dropdownItems: DropdownItem[]) => templateCaseActions.loadDropdownItemsSuccess(dropdownItems),
        failure: () => templateCaseActions.loadDropdownItemsFailure(),
        failureMessage: 'Could not load Template Case dropdown items',
      })
    ))
  ));

  loadFeatureColumns$ = createEffect(() => this.actions$.pipe(
    ofType(templateCaseActions.loadFeatureColumns),
    switchMap(() => this.api.templateCaseColumns().pipe(
      this.handler.effectResponse({
        retry: templateCaseActions.loadFeatureColumns(),
        success: (featureColumns: FeatureColumn[]) => templateCaseActions.loadFeatureColumnsSuccess(featureColumns),
        failure: () => templateCaseActions.loadFeatureColumnsFailure(),
        failureMessage: 'Could not load Template Case feature columns',
      })
    ))
  ));

  loadTemplateFeatures$ = createEffect(() => this.actions$.pipe(
    ofType(templateCaseActions.loadTemplateFeatures),
    switchMap(() => this.api.templateCaseFeatures().pipe(
      this.handler.effectResponse({
        retry: templateCaseActions.loadTemplateFeatures(),
        success: (templateFeatures: TemplateFeature[]) => templateCaseActions.loadTemplateFeaturesSuccess(templateFeatures),
        failure: () => templateCaseActions.loadTemplateFeaturesFailure(),
        failureMessage: 'Could not load Template Case features',
      })
    ))
  ));

  loadTemplateTabs$ = createEffect(() => this.actions$.pipe(
    ofType(templateCaseActions.loadTemplateTabs),
    switchMap(() => this.api.templateCaseTabs().pipe(
      this.handler.effectResponse({
        retry: templateCaseActions.loadTemplateTabs(),
        success: (templateTabs: TemplateTab[]) => templateCaseActions.loadTemplateTabsSuccess(templateTabs),
        failure: () => templateCaseActions.loadTemplateTabsFailure(),
        failureMessage: 'Could not load Template Case tabs',
      })
    ))
  ));

  loadTemplateDropdownFields$ = createEffect(() => this.actions$.pipe(
    ofType(templateCaseActions.loadTemplateDropdownFields),
    switchMap(() => this.api.templateCaseDropdownFields().pipe(
      this.handler.effectResponse({
        retry: templateCaseActions.loadTemplateDropdownFields(),
        success: (dropdownFields: DropdownField[]) => templateCaseActions.loadTemplateDropdownFieldsSuccess(dropdownFields),
        failure: () => templateCaseActions.loadTemplateDropdownFieldsFailure(),
        failureMessage: 'Could not load Template Case dropdown fields',
      })
    ))
  ));

  loadMethodologyGroups$ = createEffect(() => this.actions$.pipe(
    ofType(templateCaseActions.loadMethodologyGroups),
    switchMap(() => this.api.templateCaseMethodologyGroups().pipe(
      this.handler.effectResponse({
        retry: templateCaseActions.loadMethodologyGroups(),
        success: (methodologyGroups: MethodologyGroup[]) => templateCaseActions.loadMethodologyGroupsSuccess(methodologyGroups),
        failure: () => templateCaseActions.loadMethodologyGroupsFailure(),
        failureMessage: 'Could not load Template Case methodology groups',
      })
    ))
  ));

  triggerClear$ = createEffect(() => this.actions$.pipe(
    ofType(programmeProjectSelectionUnapplied),
    map(() => templateCaseEntityActions.clearAll())
  ));
}
